import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ConsentBanner, hasConsent } from '@/components/ConsentBanner';
import { analytics, disableAnalytics, enableAnalytics } from '@/lib/analytics';
import {
  bootstrapRedditPixelLoaded,
  flushPendingRedditSignupCompleted,
  flushPendingRedditSignIn,
} from '@/lib/reddit-pixel';
import { useAuth } from '@/stores/use-auth';

export function AnalyticsManager() {
  const location = useLocation();
  const user = useAuth((state) => state.user);
  const [consent, setConsent] = useState<boolean | null>(() => hasConsent());
  const identifiedUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (consent === true) {
      enableAnalytics();
      return;
    }

    disableAnalytics();
    identifiedUserIdRef.current = null;
  }, [consent]);

  useEffect(() => {
    if (consent !== true) return;
    bootstrapRedditPixelLoaded();
    flushPendingRedditSignupCompleted();
    flushPendingRedditSignIn();
  }, [consent]);

  useEffect(() => {
    if (consent !== true) return;

    analytics.capture('$pageview', {
      path: location.pathname,
      search: location.search,
      hash: location.hash,
      url: `${location.pathname}${location.search}${location.hash}`,
    });
  }, [consent, location.hash, location.pathname, location.search]);

  useEffect(() => {
    if (consent !== true) return;

    if (user) {
      analytics.identify(user.userId, {
        email: user.email,
        displayName: user.displayName ?? undefined,
        organizationId: user.organization?.organizationId,
        organizationName: user.organization?.name,
        organizationRole: user.organization?.role,
      });
      identifiedUserIdRef.current = user.userId;
      return;
    }

    if (identifiedUserIdRef.current) {
      analytics.reset();
      identifiedUserIdRef.current = null;
    }
  }, [consent, user]);

  return <ConsentBanner onConsentChange={setConsent} />;
}
